import React, { useState, useEffect, SyntheticEvent, useRef } from "react";
import AdminLayout from "../../layout/AdminLayout";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Typography,
  Alert,
  Tabs,
  Tab
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import VideogameAssetIcon from "@mui/icons-material/VideogameAsset";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadIcon from '@mui/icons-material/Upload';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/tr";
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import AdminField from "../../layout/AdminField";
import {
  initializeNewCampaign,
  initializeCampaignData,
} from "../../utils/initializeCampaign";
import { v4 as uuid } from "uuid";
import agent from "../../api/agent";
import { toast } from "react-toastify";
import ImagePickerModal from "../../layout/ImagePickerModal";
import VideoPickerModal from "../../layout/VideoPickerModal";
import Swal from "sweetalert2";
import { basename } from "../../utils/env";
import CampaignFields from "./main/CampaignFields";
import FormCustomization from "./main/FormCustomization";
import ScreenStyleSettings from "./main/ScreenStyleSettings";
import * as XLSX from "xlsx";

export default function QuizCampaignDetails() {
  const { id } = useParams<{ id: string }>();
  const [campaign, setCampaign] = useState<any | null>(null);
  const [titleInput, setTitleInput] = useState<string>("");
  const [tabValue, setTabValue] = useState(0);
  const [errors, setErrors] = useState<any>({});
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [activeField, setActiveField] = useState<string | null>(null);
  const [openVideoModal, setOpenVideoModal] = useState<boolean>(false);
  const [activeVideoField, setActiveVideoField] = useState<string | null>(null);

  const [iframeSrc, setIframeSrc] = useState<string | null>(null);
  const [iframeSize, setIframeSize] = useState({ width: 360, height: 640 });
  const [isLoaded, setIsLoaded] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [submitType, setSubmitType] = useState<string | null>(null);

  const formRef = useRef<HTMLFormElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const navigate = useNavigate();
  const gameScreenUrl = `${basename}/game/${id || campaign?.id}?gameType=${campaign?.gameType || 6}`;

  useEffect(() => {
    if (id) {
      agent.Campaigns.details(id).then((response) => {
        const campaignData: any = response;
        setCampaign(initializeCampaignData(campaignData));
        setTitleInput(campaignData.title);
        console.log(campaignData);
      });
    } else {
      const newCampaign = initializeNewCampaign();
      setCampaign(newCampaign);
      setTitleInput(newCampaign.title);
    }
  }, [id]);

  useEffect(() => {
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  }, [submitType]);

  const handleTabChange = (event: SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleQuestionChange = (
    index: number,
    field: string,
    value: string | boolean | number | null
  ) => {
    if (!campaign) return;

    setIsDirty(false);

    const newQuestions = [...campaign.quiz.questions];
    newQuestions[index] = { ...newQuestions[index], [field]: value };
    setCampaign({
      ...campaign,
      quiz: { ...campaign.quiz, questions: newQuestions },
    });
  };

  const handleFieldChange = (field: string, value: any) => {
    setIsDirty(false);
    setCampaign({ ...campaign, [field]: value });
  };

  const handleScreenStyleChange = (field: string, value: any) => {
    if (!campaign) return;

    setIsDirty(false);

    setCampaign({
      ...campaign,
      screenStyle: { ...campaign.screenStyle, [field]: value },
    });
  };

  const handleQuizChange = (field: string, value: any) => {
    if (!campaign) return;

    setIsDirty(false);

    setCampaign({
      ...campaign,
      quiz: { ...campaign.quiz, [field]: value },
    });
  };

  const handleFormStateChange = (field: string, value: any) => {
    if (!campaign) return;

    setIsDirty(false);

    setCampaign({
      ...campaign,
      formState: { ...campaign.formState, [field]: value },
    });
  };

  const handleAnswerChange = (
    qIndex: number,
    aIndex: number,
    field: "text" | "isCorrect",
    value: string | boolean
  ) => {
    if (!campaign) return;
    const questions = [...(campaign.quiz?.questions || [])];
    const answers = [...(questions[qIndex]?.answers || [])];

    if (field === "isCorrect") {
      // enforce single correct answer
      const newAnswers = answers.map((ans, i) => ({
        ...ans,
        isCorrect: i === aIndex ? Boolean(value) : false,
      }));
      questions[qIndex] = { ...questions[qIndex], answers: newAnswers };
    } else {
      const newAnswers = answers.map((ans, i) =>
        i === aIndex ? { ...ans, text: String(value) } : ans
      );
      questions[qIndex] = { ...questions[qIndex], answers: newAnswers };
    }

    setCampaign({
      ...campaign,
      quiz: { ...campaign.quiz, questions },
    });
  };

  const addAnswer = (qIndex: number) => {
    if (!campaign) return;
    const questions = [...(campaign.quiz?.questions || [])];
    const answers = [...(questions[qIndex]?.answers || [])];
    if (answers.length >= 4) {
      toast.warn("Bir soruda en fazla 4 cevap olabilir.");
      return;
    }
    const questionId = questions[qIndex].id || uuid();
    answers.push({
      id: uuid(),
      text: "",
      isCorrect: answers.length === 0 ? true : false, // first defaults to correct
      questionId,
    });
    questions[qIndex] = { ...questions[qIndex], id: questionId, answers };
    setCampaign({
      ...campaign,
      quiz: { ...campaign.quiz, questions },
    });
  };

  const removeAnswer = (qIndex: number, aIndex: number) => {
    if (!campaign) return;
    const questions = [...(campaign.quiz?.questions || [])];
    const answers = [...(questions[qIndex]?.answers || [])];
    if (answers.length <= 2) {
      toast.warn("Bir soruda en az 2 cevap olmalı.");
      return;
    }
    answers.splice(aIndex, 1);
    // ensure still exactly one correct (fallback first to correct if none)
    if (!answers.some(a => a.isCorrect)) {
      if (answers.length) answers[0].isCorrect = true;
    }
    questions[qIndex] = { ...questions[qIndex], answers };
    setCampaign({
      ...campaign,
      quiz: { ...campaign.quiz, questions },
    });
  };
  
  const triggerExcelPicker = () => fileInputRef.current?.click();
  const handleExcelUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = e.target.files?.[0];
      if (!file) return;

      const data = await file.arrayBuffer();
      const wb = XLSX.read(data);
      const firstSheetName = wb.SheetNames[0];
      const ws = wb.Sheets[firstSheetName];

      // rows as arrays: [[q, correct, wrong1, wrong2, wrong3], ...]
      const rows = XLSX.utils.sheet_to_json<any[]>(ws, { header: 1, raw: false }) as any[][];

      let imported = 0;
      const quizId = campaign?.quiz?.id || uuid();
      const withCategory = Boolean(campaign?.quiz?.hasCategory);

      const newQuestions = (rows || [])
        .map((r) => (Array.isArray(r) ? r : []))
        .map((r) => r.slice(0, 6)) // keep up to 5 cols (Q + 4 answers)
        .filter((r) => {
          if (withCategory) {
            // need at least: category + question + correct + 1 wrong = 4 items
            const catOk = r[0] !== undefined && String(r[0]).trim() !== "" && !isNaN(Number(r[0]));
            const qOk   = r[1] && String(r[1]).trim() !== "";
            const corr  = r[2] && String(r[2]).trim() !== "";
            const wrongExists = r.slice(3).some(x => x && String(x).trim() !== "");
            return catOk && qOk && corr && wrongExists;
          } else {
            // need at least: question + correct + 1 wrong = 3 items
            const qOk   = r[0] && String(r[0]).trim() !== "";
            const corr  = r[1] && String(r[1]).trim() !== "";
            const wrongExists = r.slice(2).some(x => x && String(x).trim() !== "");
            return qOk && corr && wrongExists;
          }
        })
        .map((r) => {
          const qId = uuid();

          const difficulty = withCategory ? Number(r[0]) : undefined;
          const qText      = String(r[withCategory ? 1 : 0]).trim();
          const correct    = String(r[withCategory ? 2 : 1]).trim();

          // wrongs
          const wrongStart = withCategory ? 3 : 2;
          const wrongsRaw = r.slice(wrongStart)
            .map(x => (x == null ? "" : String(x).trim()))
            .filter(Boolean)
            .slice(0, 3); // at most 3 wrongs so total answers ≤ 4

          const answers = [
            { id: uuid(), text: correct, isCorrect: true,  questionId: qId },
            ...wrongsRaw.map(w => ({ id: uuid(), text: w, isCorrect: false, questionId: qId })),
          ];

          const qObj: any = {
            id: qId,
            quizId,
            text: qText,
            answers
          };
          // Only set difficulty if hasCategory
          if (withCategory) qObj.difficulty = difficulty;

          return qObj;
        });

      imported = newQuestions.length;

      // Append to campaign.quiz.questions (ensure quizId present)
      setCampaign((prev: any) => ({
        ...prev!,
        quiz: {
          ...prev!.quiz!,
          id: quizId,
          campaignId: prev!.id || uuid(),
          questions: [ ...(prev?.quiz?.questions || []), ...newQuestions ],
        },
      }));

      toast.success(`${imported} soru içe aktarıldı.`);
    } catch (err) {
      console.error("Excel upload failed:", err);
      toast.error("Excel dosyası okunamadı. Lütfen formatı kontrol edin.");
    } finally {
      // allow re-selecting the same file
      if (e.target) e.target.value = "";
    }
  };

  const validateForm = () => {
    let tempErrors: any = {};

    if (!titleInput) tempErrors.titleInput = "Kampanya Başlığı zorunludur";
    
    if (campaign?.quiz?.questions) {
      const qs = campaign.quiz.questions as any[];
      if (qs.some(q => !q.text)) {
        tempErrors.questions = "Soru metni soru alanı için zorunludur.";
      }
      // answers validation
      qs.forEach((q, idx) => {
        const answers = q.answers || [];
        if (answers.length < 2 || answers.length > 4) {
          tempErrors[`q_${idx}_answers`] = `Soru ${idx+1}: 2 ile 4 arasında cevap olmalı.`;
        }
        const correctCount = answers.filter((a:any) => a.isCorrect).length;
        if (correctCount !== 1) {
          tempErrors[`q_${idx}_correct`] = `Soru ${idx+1}: Tam olarak 1 doğru cevap olmalı.`;
        }
        if (answers.some((a:any) => !a.text)) {
          tempErrors[`q_${idx}_text`] = `Soru ${idx+1}: Cevap metni boş olamaz.`;
        }
      });
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (isDirty) {
      // If there are no changes, don't resubmit or show error
      if (submitType === "regularSubmit") {
        navigate("/campaigns", { state: { reload: true } });
      }
      return;
    }

    const campaignId = campaign?.id || uuid();
    const screenStyleId = campaign?.screenStyle.id || uuid();
    const quizId = campaign?.quiz.id || uuid();
    const formStateId = campaign?.formState.id || uuid();

    const campaignData = {
      ...campaign,
      id: campaignId,
      gameType: 6,
      title: titleInput,
      startDate: (campaign!.startDate as Dayjs).toISOString(),
      endDate: (campaign!.endDate as Dayjs).toISOString(),
      collectUserData:
          campaign!.collectUserData === 1
            ? 1
            : campaign!.collectUserData === 2
              ? 2
              : campaign!.collectUserData === 3
                ? 3
                : 4,
      screenStyle: {
        ...campaign!.screenStyle,
        id: screenStyleId,
        campaignId: campaignId,
      },
      quiz: {
        ...campaign!.quiz,
        id: quizId,
        campaignId: campaignId,
        questions: (campaign?.quiz?.questions || []).map((question: any) => {
          const qId = question.id || uuid();
          return {
            ...question,
            id: qId,
            quizId: quizId!,
            difficulty: (question.difficulty ?? undefined),
            answers: (question.answers || []).map((ans: any) => ({
              ...ans,
              id: ans.id || uuid(),
              questionId: qId,
              isCorrect: !!ans.isCorrect
            })),
          };
        }),
      },
      formState: {
        ...campaign!.formState,
        id: formStateId,
        campaignId: campaignId,
      },
      wheel: {},
      puzzle: {},
      scratchWin: {},
      touchWin: {},
      matchWin: {},
      catchGame: {}
    };

    try {
      if (id) {
        await agent.Campaigns.update(campaignData);
      } else {
        console.log(campaignData);
        await agent.Campaigns.create(campaignData);
      }
      if (submitType === "regularSubmit") {
        navigate("/campaigns", { state: { reload: true } });
      } else {
        setIsDirty(true);
      }
    } catch (error: any) {
      if (error.response) {
        console.error("Error response data:", error.response.data);
      } else {
        console.error("Kampanya düzenlenirken bir hata oluştu:", error.message);
      }
    }
    setSubmitType(null);
  };

  const handleShowGameScreen = async () => {
    if (!validateForm()) {
      toast.error(
        "Lütfen geri dönüp kırmızıyla işaretlenen zorunlu alanları doldurunuz!"
      );
      return;
    }

    setSubmitType("showGameScreen");

    if (!isLoaded) {
      setIframeSrc(gameScreenUrl);
      setIsLoaded(true);
    } else {
      setIframeSrc(null);
      setTimeout(() => setIframeSrc(gameScreenUrl), 100);
    }
  };

  const handleRegularSubmit = async () => {
    if (!validateForm()) {
      toast.error(
        "Lütfen geri dönüp kırmızıyla işaretlenen zorunlu alanları doldurunuz!"
      );
      return;
    }

    setSubmitType("regularSubmit");
  };

  const openImagePicker = (field: string) => {
    setActiveField(field);
    setOpenModal(true);
  };

  const openVideoPicker = (field: string) => {
    setActiveVideoField(field);
    setOpenVideoModal(true);
  };

  const handleImageSelect = (url: string) => {
    if (!campaign || !activeField) return;

    if (["modalBg", "modalInnerBg"].includes(activeField)) {
      setCampaign({ ...campaign, [activeField]: url });
    } else if (["logo", "screenBg", "screenCenterBg"].includes(activeField)) {
      handleScreenStyleChange(activeField, url);
    } else {
      handleQuizChange(activeField, url);
    }

    setOpenModal(false);
  };

  const handleVideoSelect = (url: string) => {
    if (!campaign || !activeVideoField) return;
    setCampaign({ ...campaign, [activeVideoField]: url });
    setOpenVideoModal(false);
  };

  const handleDeleteGameData = () => {
    Swal.fire({
      title: "Silmek istediğinizden emin misiniz?",
      text: "Bu kampanya için kaydedilmiş tüm oyun ve oyuncu verileri silinecektir. Silme işlemini geri alamazsınız!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Tamam",
      cancelButtonText: "İptal",
    }).then((result) => {
      if (result.isConfirmed) {
        agent.Players.deleteAll(id)
          .then(() => {
            toast.success("Tüm oyun ve oyuncu verileri silindi!");
          })
          .catch((error) => {
            toast.error("Veriler silinirken bir hata oluştu.");
          });
      }
    });
  };

  const handleSizeChange = (size: string | number, shouldStartPreview = false) => {
    const parsedSize = Number(size);
    if (!Number.isNaN(parsedSize)) {
      const isLandscape = [1, 3, 5].includes(parsedSize);
      setIframeSize(isLandscape ? { width: 640, height: 360 } : { width: 360, height: 640 });

      if (shouldStartPreview && id) {
        handleShowGameScreen();
      }
      return;
    }

    if (size === "9:16") {
      setIframeSize({ width: 360, height: 640 });
    } else if (size === "16:9") {
      setIframeSize({ width: 640, height: 360 });
    }
  };

  if (!campaign) return null;

  return (
    <AdminLayout title={campaign.title || "Quiz Kampanyası Ekle"}>
      <Button
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/campaigns")}
        sx={{ mr: 3 }}
      >
        Kampanyalara Dön
      </Button>
      {id && (
        <>
          <Button
            variant="contained"
            startIcon={<EmojiEventsIcon />}
            color="secondary"
            onClick={() => navigate(`/scoreboard/${id}?campaignTitle=${campaign.title}&gameType=${campaign.gameType}`)}
            sx={{ mr: 2 }}
          >
            Skor Tablosu
          </Button>
          <Button
            variant="contained"
            startIcon={<AnalyticsIcon />}
            color="success"
            onClick={() => navigate(`/statistics/${id}?campaignTitle=${campaign.title}&gameType=${campaign.gameType}`)}
            sx={{ mr: 2 }}
          >
            İstatistikler
          </Button>
          <Button
            variant="contained"
            startIcon={<VideogameAssetIcon />}
            color="warning"
            onClick={() => window.open(gameScreenUrl, "_blank")}
            sx={{ mr: 2 }}
          >
            Oyun Ekranı
          </Button>
          <Button
            variant="contained"
            startIcon={<DeleteIcon />}
            color="error"
            onClick={handleDeleteGameData}
          >
            Oyun ve Oyuncu Verilerini Sil
          </Button>
        </>
      )}
      {!campaign.id && (
        <Alert sx={{ my: 3 }} severity="info">
          Temel kampanya bilgilerinizi girdikten sonra kaydetmeden önce lütfen
          yan sekmelerdeki bilgileri de düzenleyiniz. Kampanyalarınızı oluşturduktan sonra ödül
          istatistiklerini inceleyebileceğiniz <strong>İstatistikler</strong>{" "}
          butonu hemen yukarıda yer alacaktır.
        </Alert>
      )}
      <Tabs
        value={tabValue}
        className="admin-tabs"
        onChange={handleTabChange}
        aria-label="basic tabs example"
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mt: 2 }}
      >
        <Tab
          label="Kampanya Alanları"
          id="simple-tab-0"
          aria-controls="simple-tabpanel-0"
        />
        <Tab
          label="Genel Görselleştirme"
          id="simple-tab-1"
          aria-controls="simple-tabpanel-1"
        />
        <Tab
          label="Quiz Alanları"
          id="simple-tab-2"
          aria-controls="simple-tabpanel-2"
        />
        <Tab
          label="SOru ve Cevap Alanları"
          id="simple-tab-3"
          aria-controls="simple-tabpanel-3"
        />
        <Tab
          label="Form Özelleştirme"
          id="simple-tab-4"
          aria-controls="simple-tabpanel-4"
        />
      </Tabs>
      <Divider sx={{ mb: 5 }} />
      <form ref={formRef} onSubmit={handleSubmit}>
        <CampaignFields
          campaign={campaign}
          setCampaign={setCampaign}
          errors={errors}
          setErrors={setErrors}
          titleInput={titleInput}
          setTitleInput={setTitleInput}
          handleFieldChange={handleFieldChange}
          openImagePicker={openImagePicker}
          openVideoPicker={openVideoPicker}
          tabValue={tabValue}
        />
        <ScreenStyleSettings
          tabValue={tabValue}
          tabOrder={1}
          campaign={campaign}
          handleScreenStyleChange={handleScreenStyleChange}
          handleSizeChange={handleSizeChange}
          handleShowGameScreen={handleShowGameScreen}
          openImagePicker={openImagePicker}
          id={id}
          iframeSrc={iframeSrc}
          isLoaded={isLoaded}
          iframeSize={iframeSize}
        />
        {campaign.quiz && (
          <>
            <div
              role="tabpanel"
              hidden={tabValue !== 2}
              id="simple-tabpanel-2"
              aria-labelledby="simple-tab-2"
            >
              <Typography variant="h5" gutterBottom sx={{ mb: 5 }}>
                Quiz Ayarları
              </Typography>
              <Grid container spacing={4} sx={{ mb: 4 }}>
                <AdminField
                  label="Kategori var mı?"
                  type="switch"
                  value={campaign.quiz.hasCategory}
                  onChange={(value) => handleQuizChange("hasCategory", value)}
                  helperText="Quiz içinde farklı kategorilerden soru grupları eklemek için işaretleyiniz."
                />
                <AdminField
                  label="Soru Adedi"
                  value={campaign.quiz.questionAmount}
                  type="number"
                  onChange={(value) => handleQuizChange("questionAmount", value)}
                  helperText="Oyunun havuzdaki sorulardan rastgele kaç soru içereceğini rakam olarak yazınız."
                />
                <AdminField
                  label="Yanlışlardan sonra devam edilsin mi?"
                  type="switch"
                  value={campaign.quiz.continueOnWrong}
                  onChange={(value) => handleQuizChange("continueOnWrong", value)}
                  helperText="Yanlış cevaplardan sonra oyunun hemen bitip bitmeyeceğini seçiniz."
                />
                <AdminField
                  label="Quiz Oyun Tipi"
                  type="radio"
                  value={campaign.quiz.quizType}
                  onChange={(value) =>
                    handleQuizChange("quizType", parseInt(value))
                  }
                  helperText="Oyunun süreye veya hamle sayısına bağlı tiplerden hangisi olarak oynanacağını seçiniz"
                  selectOptions={[
                    { label: "Süreye Bağlı", value: 1 },
                    { label: "Hamle Sayısına Bağlı", value: 2 }
                  ]}
                />
                <Grid item xs={2}>
                </Grid>
                <Grid item xs={10}>
                  <Alert severity="info">
                    <strong>Quiz Oyun Tipi</strong>'nin <strong>Süreye Bağlı</strong>  olması durumunda aşağıdaki{" "}
                    <strong>Oyun Süresi</strong> alanını boş bırakırsanız <strong>kaybetme senaryosu olmayan</strong> oyun
                    artan süre ile gösterilecektir. Süre girmeniz halinde ise girdiğiniz süreden azalarak oynanacak ve
                    süre bittiğinde oyun kaybedilmiş olacaktır. <strong>Hamle Sayısına Bağlı</strong> oyunda ise
                      <strong>Hamle Sayısı</strong> girilmediğinde toplam hamleyi artan şekilde hesaplayacak, girildiğinde
                    de hamle sayısından azalarak oynanacak oyun hamleler bittiğinde kaybedilmiş olacaktır.
                  </Alert>
                </Grid>
                <AdminField
                  label="Oyun Süresi"
                  value={campaign.quiz.gameDuration ?? ""}
                  type="number"
                  onChange={(value) => handleQuizChange("gameDuration", value)}
                  helperText="Eğer oyun süreye bağlı olarak oynanacaksa oyunu kazanmak için kaç saniye verileceğini rakam olarak giriniz"
                />
                <AdminField
                  label="Hamle Sayısı"
                  value={campaign.quiz.moves ?? ""}
                  type="number"
                  onChange={(value) => handleQuizChange("moves", value)}
                  helperText="Hamle sayısına bağlı oyunlar için oyunun kaç hamle ile sınırlanacağını rakam olarak buradan giriniz"
                />
                <AdminField
                  label="Cevap Dizilim Tipi"
                  type="radio"
                  value={campaign.quiz.columnType}
                  onChange={(value) =>
                    handleQuizChange("columnType", parseInt(value))
                  }
                  helperText="Quiz cevaplarının tek sıra mı yoksa ikili halde mi dizileceğini seçiniz"
                  selectOptions={[
                    { label: "Tek Sıra", value: 1 },
                    { label: "İkili", value: 2 }
                  ]}
                />
              </Grid>
            </div>
            <div
              role="tabpanel"
              hidden={tabValue !== 3}
              id="simple-tabpanel-3"
              aria-labelledby="simple-tab-3"
            >
              <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
                <Button
                  variant="contained"
                  onClick={triggerExcelPicker}
                  startIcon={<UploadIcon />}
                  color="success"
                  size="small"
                >
                  Excel'den Soru Yükle
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: "none" }}
                  onChange={handleExcelUpload}
                />
                <Typography variant="body2" sx={{ alignSelf: "center" }}>
                  {campaign.quiz?.hasCategory
                    ? "Format: A sütunu Kategori (sayı), B Soru, C doğru cevap, D–F yanlış cevaplar. (Min 2 cevap, max 4)"
                    : "Format: A sütunu Soru, B doğru cevap, C–E yanlış cevaplar. (Min 2 cevap, max 4)"}
                </Typography>
              </div>
              <Grid container spacing={4} sx={{ mb: 4 }}>                
                <Grid item xs={2}>
                  <Typography variant="subtitle2">Sorular</Typography>
                </Grid>
                <Grid item xs={10}>
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      {Array.isArray(campaign.quiz.questions) &&
                        campaign.quiz.questions.map((question: any, qIndex: any) => (
                          <div key={question.id || qIndex}>
                            <h4>Soru {qIndex + 1}</h4>
                            <Grid container spacing={3}>
                              <AdminField
                                label="Soru Metni"
                                value={question.text}
                                fullWidth={true}
                                onChange={(value) =>
                                  handleQuestionChange(qIndex, "text", value)
                                }
                                error={!!errors.questions || !!errors[`q_${qIndex}_text`]}
                                errorMessage={errors[`q_${qIndex}_text`] || errors.questions}
                              />
                              {Boolean(campaign.quiz?.hasCategory) && (
                                <AdminField
                                  label="Kategori (sayı)"
                                  type="number"
                                  value={question.difficulty ?? ""}
                                  onChange={(value) =>
                                    handleQuestionChange(qIndex, "difficulty", value === "" ? null : Number(value))
                                  }
                                  helperText="Bu soru için kategori numarası (örn. 1 = Genel, 2 = Teknik)."
                                />
                              )}
                              {/* Answers */}
                              <Grid item xs={2}></Grid>
                              <Grid item xs={10}>
                                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                                  Cevaplar
                                </Typography>

                                {(question.answers || []).map((ans: any, aIndex: number) => (
                                  <Grid
                                    key={ans.id || aIndex}
                                    container
                                    spacing={2}
                                    alignItems="center"
                                    sx={{ mb: 1 }}
                                  >
                                    <AdminField
                                      label={`Cevap ${aIndex + 1}`}
                                      value={ans.text}
                                      fullWidth={true}
                                      onChange={(value) =>
                                        handleAnswerChange(qIndex, aIndex, "text", value)
                                      }
                                    />
                                    <AdminField
                                      label="Doğru mu?"
                                      type="switch"
                                      value={!!ans.isCorrect}
                                      onChange={(value) =>
                                        handleAnswerChange(qIndex, aIndex, "isCorrect", value)
                                      }
                                    />
                                    <Button
                                      variant="outlined"
                                      color="error"
                                      size="small"
                                      onClick={() => removeAnswer(qIndex, aIndex)}
                                      sx={{ ml: 2 }}
                                    >
                                      Sil
                                    </Button>
                                  </Grid>
                                ))}

                                <div style={{ marginTop: 20 }}>
                                  <Button variant="outlined" onClick={() => addAnswer(qIndex)}>
                                    Cevap Ekle
                                  </Button>
                                  {(errors[`q_${qIndex}_answers`] || errors[`q_${qIndex}_correct`]) && (
                                    <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                      {errors[`q_${qIndex}_answers`] || errors[`q_${qIndex}_correct`]}
                                    </Typography>
                                  )}
                                </div>
                              </Grid>
                              <Grid item xs={12} sx={{ mt: 1 }}>
                                <Button
                                  variant="outlined"
                                  color="error"
                                  onClick={() => {
                                    const updatedQuestions = campaign.quiz.questions.filter((_: any, i: number) => i !== qIndex);
                                    setCampaign({
                                      ...campaign,
                                      quiz: {
                                        ...campaign.quiz,
                                        questions: updatedQuestions,
                                      },
                                    });
                                  }}
                                >
                                  Soruyu Sil
                                </Button>
                              </Grid>
                            </Grid>
                            <Divider sx={{ my: 3 }} />
                          </div>
                        ))}
                      <Grid item xs={12}>
                       <Button
                          variant="outlined"
                          onClick={() => {
                            const quizId = campaign?.quiz?.id || uuid();
                            console.log(quizId);
                            setCampaign((prev : any) => ({
                              ...prev!,
                              quiz: {
                                ...prev!.quiz!,
                                id: quizId,
                                campaignId: prev!.id || uuid(), // ensure the parent campaignId exists
                                questions: [
                                  ...(prev?.quiz?.questions || []),
                                  {
                                    id: uuid(),
                                    quizId: quizId,
                                    text: "",
                                    answers: []                                    
                                  },
                                ],
                              },
                            }));
                          }}
                        >
                          Yeni Soru Ekle
                        </Button>
                        {errors.options && (
                          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                            {errors.options}
                          </Typography>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </div>
            <FormCustomization
              tabValue={tabValue}
              tabOrder={4}
              campaign={campaign}
              handleFormStateChange={handleFormStateChange}
            />
          </>
        )}
        <Divider />
        <Button
          variant="contained"
          color="primary"
          type="button"
          onClick={handleRegularSubmit}
          sx={{ my: 4 }}
        >
          Kaydet
        </Button>
      </form>
      <ImagePickerModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSelect={handleImageSelect}
      />
      <VideoPickerModal
        open={openVideoModal}
        onClose={() => setOpenVideoModal(false)}
        onSelect={handleVideoSelect}
      />
    </AdminLayout>
  );
}
